/**
 * ConsultationsChart Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A visual bar chart component that displays consultation data categorized by type.
 * Shows dynamic bars for Consultations, Papers, and Submissions with a central total circle.
 * The bar widths are proportional to the count of each category.
 * 
 * Features:
 * - Dynamic bar width calculation based on category counts
 * - Central total circle showing overall count
 * - Responsive design with visual indicators
 * - Real-time updates when data changes
 * - Color-coded categories (Consultations, Papers, Submissions)
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of consultation objects to analyze and display
 * @returns {JSX.Element} The rendered bar chart component
 * 
 * @example
 * ```jsx
 * <ConsultationsChart data={consultationData} />
 * ```
 */

import React, { useEffect, useState } from 'react';
import './ConsultationsChart.css'; 

const ConsultationsChart = ({ data }) => {
    const [totalConsultations, setTotalConsultations] = useState(0);
    const [totalSubmissions, setTotalSubmissions] = useState(0);
    const [totalPapers, setTotalPapers] = useState(0);

    /**
     * Calculate category totals and update state
     * 
     * Processes the consultation data to count items by category and updates
     * the component state for rendering. Runs whenever the data prop changes.
     * 
     * Categories counted:
     * - Consultation: Items with category = 'Consultation'
     * - Paper: Items with category = 'Paper'  
     * - Submission: Items with category = 'Submission'
     */
    useEffect(() => {
        // Calculate the totals for each category whenever data changes
        const consultationsCount = data.filter(item => item.category === 'Consultation').length;
        const papersCount = data.filter(item => item.category === 'Paper').length;
        const submissionsCount = data.filter(item => item.category === 'Submission').length;
        setTotalConsultations(consultationsCount);
        setTotalPapers(papersCount);
        setTotalSubmissions(submissionsCount);

    }, [data]); // Dependency on data to recalculate counts

    const total = totalConsultations + totalPapers + totalSubmissions;

    return (
        <div className="bar-chart-container">
            <div className='bar-bars'>
                {/* Consultations Bar */}
                <div className="bar-wrapper">
                    <div className="bar-static bar-consultation">Consultations</div>
                    <div className="bar-dynamic bar-consultation-dynamic" style={{ width: `${(totalConsultations / total) * 105}%` }}>
                        {totalConsultations}
                    </div>
                </div>

                {/* Papers Bar */}
                <div className="bar-wrapper">
                    <div className="bar-static bar-paper">Papers</div>
                    <div className="bar-dynamic bar-paper-dynamic" style={{ width: `${(totalPapers / total) * 105}%` }}>
                        {totalPapers}
                    </div>
                </div>

                {/* Submissions Bar */}
                <div className="bar-wrapper">
                    <div className="bar-static bar-submission">Submissions</div>
                    <div className="bar-dynamic bar-submission-dynamic" style={{ width: `${(totalSubmissions / total) * 105}%` }}>
                        {totalSubmissions}
                    </div>
                </div>
            </div>
            <div className='bar-totals'>
                {/* Total Circle */}
                <div className="circle-container">
                    <div className="total-circle">
                        <span className="circle-text">{total}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConsultationsChart;
